import {
    InvestmentManagementAccountType,
    MoneyMapAccountStatus,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapRuleType,
    NEW_APEX_IM_ACCOUNT_OPTION,
    NEW_IM_ACCOUNT_OPTION,
    ReadableInvestmentManagementAccountType,
} from '@fruitful/types';
import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { ControlledCheckbox, Notice } from 'smoothie';

import {
    findUnlinkedRothIrasForTrad,
    findUnlinkedTradIrasForRoth,
} from '../../utils/goalFormBackdoorRothUtils';
import {
    AccountSettingsFormModule,
    type GenericAccountSettingsFormSchema,
    getBackdoorTradApexAccountTypeLabelValueOptions,
} from './AccountSettingsFormModule';

type FormWithBackdoor = GenericAccountSettingsFormSchema & {
    isBackdoorRoth?: boolean;
};

interface BackdoorRothAccountFieldsProps {
    accounts: MoneyMapAccountWithAccountDetails[];
    formFunctions: UseFormReturn<FormWithBackdoor>;
    ruleType: MoneyMapRuleType;
    shouldShowBackdoorCheckbox: boolean;
    shouldShowBackdoorTradPanel: boolean;
    shouldDisableBackdoorCheckbox: boolean;
    /** Lock the entire Trad panel (checkbox + account picker) because the Roth is already paired. */
    shouldForceBackdoorRothChecked?: boolean;
    /** The currently selected primary account (Roth side). */
    selectedMoneyMapAccount: MoneyMapAccountWithAccountDetails | undefined;
    selectedMoneyMapAccountForTransfer:
        | MoneyMapAccountWithAccountDetails
        | undefined;
    /** Trad row: same semantics as GoalForm transfer preset */
    isUsingPresetTransfer: boolean;
}

/**
 * Checkbox + Trad IRA panel shared by goal and other-account rules for backdoor Roth pairing.
 */
export const BackdoorRothAccountFields = ({
    accounts,
    formFunctions,
    ruleType,
    shouldShowBackdoorCheckbox,
    shouldShowBackdoorTradPanel,
    shouldDisableBackdoorCheckbox,
    shouldForceBackdoorRothChecked = false,
    selectedMoneyMapAccount,
    selectedMoneyMapAccountForTransfer,
    isUsingPresetTransfer,
}: BackdoorRothAccountFieldsProps) => {
    const transferMoneyMapAccountId = formFunctions.watch(
        'transferAccountSettings.moneyMapAccountId',
    );

    const transferAccountForNotice = useMemo(
        () => accounts.find((a) => a.id === transferMoneyMapAccountId),
        [accounts, transferMoneyMapAccountId],
    );

    const unlinkedTradIras = useMemo(
        () => findUnlinkedTradIrasForRoth(selectedMoneyMapAccount, accounts),
        [selectedMoneyMapAccount, accounts],
    );
    const showUnlinkedTradNotice =
        shouldShowBackdoorCheckbox &&
        !shouldShowBackdoorTradPanel &&
        unlinkedTradIras.length > 0;

    const unlinkedRothIras = useMemo(
        () => findUnlinkedRothIrasForTrad(selectedMoneyMapAccount, accounts),
        [selectedMoneyMapAccount, accounts],
    );
    const showUnlinkedRothNotice =
        !shouldShowBackdoorCheckbox && unlinkedRothIras.length > 0;

    const isProposedBackdoor =
        shouldShowBackdoorTradPanel &&
        !shouldForceBackdoorRothChecked &&
        (selectedMoneyMapAccount?.status !== MoneyMapAccountStatus.EXISTS ||
            transferMoneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value ||
            transferMoneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value ||
            transferAccountForNotice?.status !== MoneyMapAccountStatus.EXISTS);

    return (
        <>
            {showUnlinkedTradNotice && (
                <Notice
                    variant="warning"
                    title="Looking to set up a backdoor Roth?"
                    description={`This member has both a Roth IRA and ${unlinkedTradIras.length === 1 ? 'a Traditional/Rollover IRA' : `${unlinkedTradIras.length} Traditional/Rollover IRAs`} that are not linked for backdoor transfers. Send a message to #im-squad-pii with member ID and account numbers if you want to setup automatic backdoor Roth conversions.`}
                />
            )}
            {showUnlinkedRothNotice && (
                <Notice
                    variant="warning"
                    title="Looking to set up a backdoor Roth?"
                    description={`This member has both a Traditional/Rollover IRA and ${unlinkedRothIras.length === 1 ? 'a Roth IRA' : `${unlinkedRothIras.length} Roth IRAs`} that are not linked for backdoor transfers. Send a message to #im-squad-pii with member ID and account numbers if you want to setup automatic backdoor Roth conversions.`}
                />
            )}
            {shouldShowBackdoorCheckbox && (
                <ControlledCheckbox
                    name="isBackdoorRoth"
                    control={formFunctions.control}
                    register={formFunctions.register}
                    errors={formFunctions.formState.errors}
                    label="Backdoor Roth (pair with a Traditional or Rollover IRA)"
                    disabled={shouldDisableBackdoorCheckbox}
                />
            )}
            {shouldShowBackdoorTradPanel && (
                <>
                    {isProposedBackdoor && (
                        <Notice
                            variant="warning"
                            title="Pro rata rule"
                            description="Before setting up a backdoor Roth, confirm with the member that they do not have existing pre-tax money in any Traditional, SEP, or SIMPLE IRA. Pre-tax balances trigger the pro rata rule and may result in unexpected taxes on the conversion."
                        />
                    )}
                    <AccountSettingsFormModule
                        type="transferAccount"
                        accounts={accounts}
                        selectedAccount={selectedMoneyMapAccountForTransfer}
                        formFunctions={
                            formFunctions as never as UseFormReturn<GenericAccountSettingsFormSchema>
                        }
                        isUsingPreset={isUsingPresetTransfer}
                        ruleType={ruleType}
                        transferFormModuleTitle="Traditional IRA (backdoor pair)"
                        transferAccountPickerFilter="backdoorTradEligible"
                        apexImFieldsMode="backdoorTradNa"
                        apexAccountTypeOptionsOverride={getBackdoorTradApexAccountTypeLabelValueOptions()}
                        imAccountTypeOptionsOverride={[
                            {
                                label: ReadableInvestmentManagementAccountType[
                                    InvestmentManagementAccountType.IRA
                                ],
                                value: InvestmentManagementAccountType.IRA,
                            },
                        ]}
                        disableAccountPicker={shouldForceBackdoorRothChecked}
                    />
                </>
            )}
        </>
    );
};
